import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { KeyRound, CheckCircle, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ResendManageLink = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setError(t('classifieds.resendErrEmail', 'Please enter a valid email'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/api/classifieds/resend-manage-links?email=${encodeURIComponent(email.trim())}`);
      setSent(true);
    } catch {
      setError(t('classifieds.resendError', 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-emerald-800" data-testid="resend-success">{t('classifieds.resendSent', 'Check your email!')}</p>
            <p className="text-xs text-emerald-600 mt-0.5">{t('classifieds.resendSentDesc', 'If you have any active listings, we\'ve sent the manage links to your email.')}</p>
          </div>
          <button onClick={() => { setSent(false); setOpen(false); setEmail(''); }} className="ml-auto text-emerald-400 hover:text-emerald-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors mb-4 w-full sm:w-auto"
        data-testid="open-resend-link-btn"
      >
        <KeyRound className="h-4 w-4" />
        {t('classifieds.resendLink', 'Lost your manage link?')}
      </button>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 shadow-sm" data-testid="resend-form">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-gray-600" />
          {t('classifieds.resendTitle', 'Get your manage links')}
        </h3>
        <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
      </div>
      <p className="text-xs text-gray-500 mb-3">{t('classifieds.resendDesc', 'Enter the email you used when posting. We\'ll resend your manage links.')}</p>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(''); }}
          placeholder={t('classifieds.resendEmailPlaceholder', 'your@email.com')}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
          data-testid="resend-email-input"
        />
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
          data-testid="resend-submit-btn"
        >
          {submitting ? '...' : t('classifieds.resendBtn', 'Send')}
        </button>
      </form>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
    </div>
  );
};

export default ResendManageLink;
